import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Trading oversight (#10) — READ-ONLY. Shows agent positions (recommandation table)
// and which alerts are agent-trading-enabled. No trade controls here by design:
// enabling/disabling live trading touches real money and is intentionally out of scope.

const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
// Growth values are stored as fractions (0.081 = 8.1%).
const pct = (n) => (n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`);

const AdminTrading = ({ embedded = false }) => {
  const [positions, setPositions] = useState([]);
  const [agentAlerts, setAgentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  // Per-column filters: { [colKey]: string }. Text cols = "contains"; numeric cols = ">= value".
  const [colFilters, setColFilters] = useState({});
  // Quick toggles
  const [revPos, setRevPos] = useState(false); // revenue growth > 0
  const [epsPos, setEpsPos] = useState(false); // EPS growth > 0

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axios.get('/admin/trading/positions'),
        axios.get('/admin/trading/agent-alerts'),
      ]);
      setPositions(Array.isArray(p.data) ? p.data : []);
      setAgentAlerts(Array.isArray(a.data) ? a.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load trading data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Column definitions drive the header, filter row, and body cells.
  const columns = [
    { key: 'symbol', label: 'Symbol', type: 'text' },
    { key: 'status', label: 'Status', type: 'text' },
    { key: 'interval', label: 'Interval', type: 'text' },
    { key: 'buyrule', label: 'Buy rule', type: 'text' },
    { key: 'sellrule', label: 'Sell rule', type: 'text' },
    { key: 'buyprice', label: 'Buy', type: 'num', fmt: 'money' },
    { key: 'stoploss', label: 'Stop', type: 'num', fmt: 'money' },
    { key: 'sellprice', label: 'Sell', type: 'num', fmt: 'money' },
    { key: 'gain', label: 'Gain', type: 'num', fmt: 'pct0' },
    { key: 'rev_growth_yoy', label: 'Rev Growth', type: 'num', fmt: 'pct',
      title: 'Current quarterly revenue growth YoY (not as-of purchase)' },
    { key: 'eps_growth_yoy', label: 'EPS Growth', type: 'num', fmt: 'pct',
      title: 'Current quarterly EPS growth YoY (not as-of purchase)' },
    { key: 'agent_managed', label: 'Agent', type: 'text' },
  ];

  const setColFilter = (key, val) => setColFilters((f) => ({ ...f, [key]: val }));
  const clearFilters = () => { setColFilters({}); setRevPos(false); setEpsPos(false); };

  const matchesColumn = (p, col) => {
    const raw = colFilters[col.key];
    if (raw == null || raw === '') return true;
    const v = p[col.key];
    if (col.type === 'num') {
      const min = parseFloat(raw);
      if (Number.isNaN(min)) return true;
      if (v == null || v === '') return false;
      // Growth columns are fractions (0.08 = 8%); compare the filter as a percentage.
      const cmp = (col.key === 'rev_growth_yoy' || col.key === 'eps_growth_yoy') ? Number(v) * 100 : Number(v);
      return cmp >= min;
    }
    // text: contains (agent_managed shown as "agent"/"")
    const text = col.key === 'agent_managed' ? (v ? 'agent' : '') : (v == null ? '' : String(v));
    return text.toLowerCase().includes(String(raw).toLowerCase());
  };

  const shown = positions.filter((p) => {
    if (!showClosed && (p.status || '').toUpperCase() !== 'OPEN') return false;
    if (revPos && !(Number(p.rev_growth_yoy) > 0)) return false;
    if (epsPos && !(Number(p.eps_growth_yoy) > 0)) return false;
    return columns.every((col) => matchesColumn(p, col));
  });
  const openCount = positions.filter((p) => (p.status || '').toUpperCase() === 'OPEN').length;

  const renderCell = (p, col) => {
    const v = p[col.key];
    if (col.key === 'status') {
      const open = (p.status || '').toUpperCase() === 'OPEN';
      return <span className={`inline-block px-2 py-0.5 rounded text-xs ${open ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{p.status || '—'}</span>;
    }
    if (col.key === 'agent_managed') return p.agent_managed ? <span className="text-xs text-indigo-600">agent</span> : '';
    if (col.fmt === 'money') return money(v);
    if (col.fmt === 'pct0') return v != null ? `${v}%` : '—';
    if (col.fmt === 'pct') return pct(v);
    return v == null || v === '' ? '—' : v;
  };

  const cellClass = (p, col) => {
    if (col.key === 'gain') return p.gain > 0 ? 'text-green-600' : p.gain < 0 ? 'text-red-600' : '';
    if (col.key === 'rev_growth_yoy') return p.rev_growth_yoy > 0 ? 'text-green-600' : p.rev_growth_yoy < 0 ? 'text-red-600' : 'text-gray-500';
    if (col.key === 'eps_growth_yoy') return p.eps_growth_yoy > 0 ? 'text-green-600' : p.eps_growth_yoy < 0 ? 'text-red-600' : 'text-gray-500';
    if (col.key === 'symbol') return 'font-medium';
    if (col.key === 'buyrule' || col.key === 'sellrule' || col.key === 'stoploss') return 'text-gray-500';
    return '';
  };

  const inner = (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          {!embedded && <h1 className="text-2xl font-bold text-gray-900">Trading Oversight</h1>}
          <p className="text-sm text-gray-500">Agent positions &amp; enabled strategies (read-only)</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 flex items-center gap-1">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            Show closed
          </label>
          <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded">Refresh</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="bg-white rounded-lg shadow-sm px-4 py-2"><b className="text-xl text-green-600">{openCount}</b> <span className="text-sm text-gray-500">open positions</span></div>
        <div className="bg-white rounded-lg shadow-sm px-4 py-2"><b className="text-xl">{positions.length}</b> <span className="text-sm text-gray-500">total recorded</span></div>
        <div className="bg-white rounded-lg shadow-sm px-4 py-2"><b className="text-xl text-indigo-600">{agentAlerts.length}</b> <span className="text-sm text-gray-500">agent-enabled alerts</span></div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
        <div className="px-4 py-2 border-b border-gray-100 font-semibold text-gray-900 flex items-center justify-between">
          <span>Positions (recommandation)</span>
          <span className="text-xs font-normal text-gray-400">{shown.length} shown</span>
        </div>
        {/* Quick toggles */}
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-1 text-gray-700">
            <input type="checkbox" checked={revPos} onChange={(e) => setRevPos(e.target.checked)} />
            Rev growth positive
          </label>
          <label className="flex items-center gap-1 text-gray-700">
            <input type="checkbox" checked={epsPos} onChange={(e) => setEpsPos(e.target.checked)} />
            EPS growth positive
          </label>
          <button onClick={clearFilters} className="px-2 py-1 text-gray-600 border border-gray-300 rounded hover:bg-white">Clear all filters</button>
          <span className="text-xs text-gray-400">Numeric filters match ≥ value; text filters match “contains”.</span>
        </div>
        <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600 sticky top-0">
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} className="px-3 py-2 whitespace-nowrap" title={col.title}>{col.label}</th>
                  ))}
                </tr>
                <tr className="bg-white">
                  {columns.map((col) => (
                    <th key={col.key} className="px-2 py-1">
                      <input
                        type={col.type === 'num' ? 'number' : 'text'}
                        value={colFilters[col.key] || ''}
                        onChange={(e) => setColFilter(col.key, e.target.value)}
                        placeholder={col.type === 'num' ? '≥' : 'filter'}
                        className="w-full min-w-[60px] px-1.5 py-0.5 border border-gray-200 rounded text-xs font-normal"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((p, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    {columns.map((col) => (
                      <td key={col.key} className={`px-3 py-2 ${cellClass(p, col)}`}>{renderCell(p, col)}</td>
                    ))}
                  </tr>
                ))}
                {shown.length === 0 && <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-gray-500">No positions match the filters.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-100 font-semibold text-gray-900">Agent-enabled strategies</div>
        <div className="overflow-x-auto max-h-[40vh] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600 sticky top-0">
              <tr>
                <th className="px-3 py-2">Symbol</th>
                <th className="px-3 py-2">Event type</th>
                <th className="px-3 py-2">Interval</th>
                <th className="px-3 py-2">Max %</th>
                <th className="px-3 py-2">Mode</th>
              </tr>
            </thead>
            <tbody>
              {agentAlerts.map((a, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium">{a.symbol}</td>
                  <td className="px-3 py-2 text-gray-600">{a.eventtype || a.eventType}</td>
                  <td className="px-3 py-2 text-gray-500">{a.interval}</td>
                  <td className="px-3 py-2">{a.agent_max_position_pct ?? a.agentMaxPositionPct ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${(a.agent_trading_mode || a.agentTradingMode) === 'LIVE' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {a.agent_trading_mode || a.agentTradingMode || 'PAPER'}
                    </span>
                  </td>
                </tr>
              ))}
              {agentAlerts.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">No agent-enabled alerts.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-3">
        Read-only. Enabling/disabling live trading is intentionally not available here — it touches real money.
      </p>
    </>
  );

  if (embedded) return inner;
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">{inner}</div>
    </div>
  );
};

export default AdminTrading;
